import { formatDateWithSuperscript } from "../../helpers/Common";
import { useMediaQuery } from 'react-responsive';
import { ReactComponent as Calender } from '../../assets/svg/calendar.svg';
import { Tabs } from "../../components/Shared/Tabs";
import { Button } from "../Shared/Buttons/Button";
import { ReactComponent as LeftArrow } from "../../assets/svg/left_arrow.svg";
import { ReactComponent as Crown } from '../../assets/svg/crown.svg';
import { ReactComponent as Arrow } from '../../assets/svg/drop_down.svg';
import { ReactComponent as TickMark } from '../../assets/svg/tick_mark.svg';
import { useEffect, useRef, useState } from "react";
import { JobApplication } from "../../models/JobApplication";
import { LazyProfileImage } from "../Shared/LazyProfileImage";
import { ApplicationStatusPageTexts } from "../../helpers/LayoutTexts";

interface Props {
    applicant: JobApplication;
    style: any;
    tabItems: any;
    activeTab: string;
    applicationStatuses: any[];
    onStatusOptionClick: (status: any) => void;
    onClose: () => void;
}

export const ApplicationTabPanel: React.FC<Props> = (props) => {
    const styles = props.style;
    const isMobile = useMediaQuery({ query: "(max-width: 786px)" });
    const selectedApplicant = props.applicant;
    const [hoveredPremiumCandidate, setHoveredPremiumCandidate] = useState<boolean>(false);
    const [activeTab, setActiveTab] = useState<string>(props.activeTab);
    const [openStatusDropdown, setOpenStatusDropdown] = useState<boolean>(false);
    const [hoveredStatusOption, setHoveredStatusOption] = useState<number | null>(null);
    const statusDropdownRef = useRef<HTMLDivElement>(null);
    const STAGE_STATUS_MAP = ApplicationStatusPageTexts.STAGE_STATUS_MAP;

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    useEffect(() => {
        const handleClickOutsideStatusDropdown = (event: MouseEvent) => {
            if (statusDropdownRef.current && !statusDropdownRef.current.contains(event.target as Node)) {
                setOpenStatusDropdown(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutsideStatusDropdown);
        return () => {
            document.removeEventListener('mousedown', handleClickOutsideStatusDropdown);
        };
    }, []);

    const getGroupedStatusOptions = (statuses: any[]) => {
        return Object.keys(STAGE_STATUS_MAP)
            .map((stage) => ({
                stage,
                items: statuses.filter((status) => STAGE_STATUS_MAP[stage]?.includes(status.status))
            }))
            .filter((group) => group.items.length > 0);
    };

    const groupedStatusOptions = getGroupedStatusOptions(props.applicationStatuses);

    const handleStatusOptionClick = (e: React.MouseEvent, status: any) => {
        e.stopPropagation();
        setOpenStatusDropdown(false);
        setHoveredStatusOption(null);
        props.onStatusOptionClick(status);
    };

    function lightenColor(hex: string, alpha: number): string {
        // Remove any leading/trailing whitespace from the hex string
        hex = hex.trim();
        // Converts a hex color string to an rgba color string with given transparency
        const r = parseInt(hex.slice(1, 3), 16);
        const g = parseInt(hex.slice(3, 5), 16);
        const b = parseInt(hex.slice(5, 7), 16);
        return `rgba(${r}, ${g}, ${b}, ${alpha})`;
    }

    return (
        <div style={styles.content}>
            {isMobile && <div style={styles.backButton} onClick={() => props.onClose()}>
                <Button style={styles.backButton.button}><LeftArrow style={styles.backButton.icon} /></Button>
                <span style={styles.backButton.text}>{"Back"}</span>
            </div>}
            {selectedApplicant?.candidate && (      
                <div>
                    {selectedApplicant && <div style={styles.profileContainer}>
                        <LazyProfileImage
                            candidateId={props.applicant.candidate?.candidateId!}                                                                        
                            style={styles.profileImage}
                        />
                        <div style={styles.detailsWrapper}>
                            <div style={styles.detailsWrapper.nameWrapper}>
                                <div style={styles.detailsWrapper.name}>{selectedApplicant?.candidate.forename} {selectedApplicant?.candidate.surname}</div>
                                {selectedApplicant.candidate.isPremium && 
                                    <div style={styles.detailsWrapper.crownWrapper}
                                        onMouseEnter={() => setHoveredPremiumCandidate(true)} 
                                        onMouseLeave={() => setHoveredPremiumCandidate(false)}
                                    >
                                        <Crown style={styles.detailsWrapper.crownIcon}/>
                                        {hoveredPremiumCandidate && <div style={styles.premiumMessage}>
                                            <div style={styles.premiumMessage.pointer}/>
                                                {"Premium Candidate"}
                                        </div>}
                                    </div>
                                }
                            </div>
                            <div style={styles.detailsWrapper.location}>{selectedApplicant?.candidate.homeTown}</div>
                            <div style={styles.detailsWrapper.appliedDate}>
                                <Calender style={styles.detailsWrapper.calendarIcon}/>
                                <div style={styles.detailsWrapper.text}>
                                    <span style={styles.detailsWrapper.applied}>{"Applied"}</span>
                                    {formatDateWithSuperscript(selectedApplicant.createdDate)}
                                </div>
                            </div>
                            <div
                                style={styles.detailsWrapper.statusBadgeContainer}
                                ref={openStatusDropdown ? statusDropdownRef : null}
                            >
                                <div 
                                    style={{
                                        ...styles.detailsWrapper.statusBadge, 
                                        color: selectedApplicant.applicationStatus?.colorCode, 
                                        backgroundColor: lightenColor(selectedApplicant.applicationStatus?.colorCode!, 0.15)
                                    }}
                                    onClick={(e) => { e.stopPropagation(); selectedApplicant.hasSentToRecruiter && setOpenStatusDropdown(prev => !prev); }}
                                >
                                    {selectedApplicant.applicationStatus?.status}
                                    {selectedApplicant.hasSentToRecruiter && (
                                        <Arrow 
                                            style={{
                                                ...styles.detailsWrapper.statusBadge.chevron, 
                                                ...(openStatusDropdown && styles.detailsWrapper.statusBadge.chevron.open)
                                            }}
                                        />
                                    )}
                                </div>
                                {openStatusDropdown && (
                                    <div className={"scrollArea"} style={styles.detailsWrapper.statusMenu} onClick={(e) => e.stopPropagation()}>
                                        {groupedStatusOptions.map((group, groupIndex) => (
                                            <div 
                                                key={group.stage} 
                                                style={{
                                                    ...styles.detailsWrapper.statusMenu.group,
                                                    ...(groupIndex === groupedStatusOptions.length - 1 && styles.detailsWrapper.statusMenu.group.lastItem)
                                                }}
                                            >
                                                <div style={styles.detailsWrapper.statusMenu.groupHeader}>
                                                    {group.stage.toUpperCase()}
                                                </div>
                                                {group.items.map((status) => {
                                                    const optionKey = status.applicationStatusId;
                                                    return (
                                                        <div
                                                            key={status.applicationStatusId}
                                                            style={{
                                                                ...styles.detailsWrapper.statusMenu.option, 
                                                                ...(status.status === selectedApplicant.applicationStatus?.status && styles.detailsWrapper.statusMenu.option.current),
                                                                ...(hoveredStatusOption === optionKey && styles.detailsWrapper.statusMenu.option.hovered)
                                                            }}
                                                            onClick={(e) => handleStatusOptionClick(e, status)}
                                                            onMouseEnter={() => setHoveredStatusOption(optionKey)}
                                                            onMouseLeave={() => setHoveredStatusOption(null)}
                                                        >
                                                            <span style={{...styles.detailsWrapper.statusMenu.dot, backgroundColor: status.colorCode}}/>
                                                            <span style={styles.detailsWrapper.statusMenu.label}>{status.status}</span>
                                                            {status.status === selectedApplicant.applicationStatus?.status && <TickMark style={styles.detailsWrapper.statusMenu.tick}/>}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>}
                    {props.tabItems.length > 0 && (
                        <Tabs 
                            styles={styles.tabs} 
                            tabItems={props.tabItems} 
                            selectedTab={activeTab}
                            getSelectedTab={setActiveTab}
                            enableScrollArrows={true}
                        />
                    )}
                </div>
            )}
        </div>
    )
}